import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Card, Col, Container, Form, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import { DocumentModel } from '@/lib/models';
import { blogsService } from '@/lib/services';
import { useHandleError, useQuery } from '@/hooks';
import { AsyncPage, Loader, LoadingButton, MultiSelect, MultiSelectOption, Select } from '@/components';
import { constructUrl, getParsedQuery } from '@/lib/utils';
import { ReactComponent as PencilIcon } from '@/assets/icons/icon-pencil.svg';

import LinesEllipsis from 'react-lines-ellipsis';
import responsiveHOC from 'react-lines-ellipsis/lib/responsiveHOC';
const ResponsiveEllipsis = responsiveHOC()(LinesEllipsis);

export const BlogPosts = () => {
	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const handleError = useHandleError();

	const query = useQuery();
	const hasQuery = useMemo(() => {
		const paramsToIgnore = ['size'];

		return (
			Object.entries(getParsedQuery(query)).filter(
				([key, value]) => !paramsToIgnore.includes(key) && value !== ''
			).length > 0
		);
	}, [query]);
	const programIds = useMemo(() => (query.get('programIds') ?? '').split('|').filter((s) => s), [query]);
	const size = useMemo(() => query.get('size') ?? '10', [query]);
	const pageRef = useRef(0);

	const [isLoading, setIsLoading] = useState(false);
	const [totalCount, setTotalCount] = useState(0);
	const [blogs, setBlogs] = useState<DocumentModel[]>([]);
	const [programOptions, setProgramOptions] = useState<MultiSelectOption[]>([]);

	const fetchFormOptions = useCallback(async () => {
		const response = await blogsService.getMyBlogsFilters().fetch();

		setProgramOptions(
			response.programs.map((program) => ({
				id: program.id,
				title: program.display,
				value: program.id,
			}))
		);
	}, []);

	const fetchBlogs = useCallback(async () => {
		setIsLoading(true);

		try {
			const blogsResponse = await blogsService.getBlogs({ page: `${pageRef.current}`, size, programIds }).fetch();

			setTotalCount(blogsResponse.totalCount);
			setBlogs((previousValue) => {
				if (pageRef.current === 0) {
					return blogsResponse.results;
				}

				return previousValue.concat(blogsResponse.results);
			});
		} catch (error) {
			handleError(error);
		} finally {
			setIsLoading(false);
		}
	}, [handleError, programIds, size]);

	useEffect(() => {
		fetchBlogs();
	}, [fetchBlogs]);

	const handlePageSizeChange = ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
		setBlogs([]);
		pageRef.current = 0;
		navigate(constructUrl(pathname, { size: currentTarget.value }, search), { replace: true });
	};

	const handleClearFiltersButtonClick = () => {
		setBlogs([]);
		pageRef.current = 0;
		navigate(constructUrl(pathname, { programIds: [] }, search), { replace: true });
	};

	const handleProgramChange = (programIds: string[]) => {
		setBlogs([]);
		pageRef.current = 0;
		navigate(constructUrl(pathname, { programIds }, search), { replace: true });
	};

	const handleShowMoreButtonClick = () => {
		pageRef.current += 1;
		fetchBlogs();
	};

	return (
		<AsyncPage fetchData={fetchFormOptions}>
			<Helmet>
				<title>Hackett Connect - Blog Posts</title>
			</Helmet>
			<Container className="py-11">
				<Row>
					<Col xs={12} xl={8} className="mb-6">
						<div className="mb-9 d-flex align-items-center justify-content-between">
							<h1 className="mb-0 text-primary">Your Blog Posts</h1>
							<div className="d-flex align-items-center">
								<div className="mr-2 d-flex align-items-center">
									<Form.Label className="mr-1 mb-0">Page Size</Form.Label>
									<Select variant="secondary" value={size} onChange={handlePageSizeChange}>
										<option value="10">10</option>
										<option value="20">20</option>
										<option value="50">50</option>
									</Select>
								</div>
								<Button
									onClick={() => {
										navigate('/blog-posts/create');
									}}
								>
									Create a Post
								</Button>
							</div>
						</div>
						{isLoading && blogs.length <= 0 && (
							<div className="mb-8 d-flex justify-content-center">
								<Loader />
							</div>
						)}
						{!isLoading && blogs.length === 0 && (
							<div className="p-10 rounded border bg-white">
								<h3 className="mb-2 text-center">No published blog posts</h3>
								<p className="mb-0 text-muted text-center">
									When you publish a blog post, it will appear here.
								</p>
							</div>
						)}
						{!isLoading && blogs.length > 0 && (
							<>
								<ul className="list-unstyled">
									{blogs.map((blog) => (
										<li key={blog.documentId} className="py-5 border-bottom">
											<div className="mb-2 d-flex align-items-center justify-content-between">
												<Link to={`/research/${blog.documentId}`} className="bold">
													{blog.name}
												</Link>
												<Button
													variant="link"
													onClick={() => {
														navigate(`/blog-posts/${blog.documentId}`);
													}}
												>
													<PencilIcon />
												</Button>
											</div>
											<ResponsiveEllipsis
												className="mb-6"
												text={blog.contentDescription ?? ''}
												maxLine="3"
												component="p"
											/>
											<div className="small">
												<span className="text-gray-600 font-weight-semi-bold">
													{blog.publicationDateDescription}
												</span>
												<span className="text-gray-600 mx-2">&bull;</span>
												<span className="text-gray-900">
													{blog.masterClientTags
														.flatMap((mc) => mc.programTags)
														.map((p) => p.programName)
														.join(', ')}
												</span>
											</div>
										</li>
									))}
								</ul>
								<div className="d-flex align-items-center justify-content-end">
									<span className="text-muted mr-4">
										Showing 1-{blogs.length}
										{hasQuery && <>&nbsp;of {totalCount}</>}
									</span>
									{blogs.length < totalCount && (
										<LoadingButton
											isLoading={isLoading}
											variant="outline-info"
											onClick={handleShowMoreButtonClick}
										>
											Show {size} More
										</LoadingButton>
									)}
								</div>
							</>
						)}
					</Col>
					<Col xs={12} xl={4}>
						<Card>
							<Card.Body>
								<div className="mb-4 d-flex align-items-end justify-content-between">
									<h3 className="mb-0">Filters</h3>
									<Button variant="link" onClick={handleClearFiltersButtonClick}>
										Clear
									</Button>
								</div>
								<Form.Group>
									<Form.Label>Programs</Form.Label>
									<MultiSelect
										options={programOptions}
										selected={programIds}
										onChange={handleProgramChange}
										disabled={isLoading}
									>
										Select
									</MultiSelect>
								</Form.Group>
							</Card.Body>
						</Card>
					</Col>
				</Row>
			</Container>
		</AsyncPage>
	);
};
